import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PRODUCT } from '../data/product';

const SITE_NAME = 'Rao Jatin Shop';
const SITE_URL = 'https://raojatin.shop';
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;
const DEFAULT_KEYWORDS =
  'Rao Jatin Shop, Divoom Ditoo Pro, pixel art speaker, bluetooth speaker India, retro desk speaker, prepaid ecommerce';

const SEO_BY_PATH = {
  '/': {
    title: 'Rao Jatin Shop | Premium Pixel Art Bluetooth Speaker in India',
    description:
      'Official Rao Jatin Shop for Divoom Ditoo-Pro. Premium pixel art Bluetooth speaker with prepaid checkout, free shipping, and creator-first desk aesthetics.',
    robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'
  },
  '/product': {
    title: 'Divoom Ditoo-Pro Pixel Art Speaker | Rao Jatin Shop',
    description:
      'Buy Divoom Ditoo-Pro Retro Pixel Art Bluetooth Speaker at Rao Jatin Shop. Explore colors, features, customer reviews, and secure prepaid checkout.',
    robots: 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1',
    type: 'product'
  },
  '/cart': {
    title: 'Your Cart | Rao Jatin Shop',
    description: 'Review selected Ditoo-Pro items in your cart before checkout.',
    robots: 'noindex,nofollow'
  },
  '/checkout': {
    title: 'Secure Checkout | Rao Jatin Shop',
    description: 'Complete your prepaid order securely for Divoom Ditoo-Pro at Rao Jatin Shop.',
    robots: 'noindex,nofollow'
  },
  '/login': {
    title: 'Login | Rao Jatin Shop Account',
    description: 'Login to Rao Jatin Shop to track orders, manage profile, and checkout faster.',
    robots: 'noindex,nofollow'
  },
  '/signup': {
    title: 'Create Account | Rao Jatin Shop',
    description: 'Create your Rao Jatin Shop account for faster checkout and order tracking.',
    robots: 'noindex,nofollow'
  },
  '/profile': {
    title: 'My Profile | Rao Jatin Shop',
    description: 'Manage your Rao Jatin Shop account profile and order-related actions.',
    robots: 'noindex,nofollow'
  }
};

function upsertMeta(attribute, key, content) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertCanonical(url) {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
}

function upsertStructuredData(pathname, seo) {
  const graph = [
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}#organization`,
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_IMAGE
      },
      sameAs: ['https://instagram.com/raojatin.in']
    },
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}#website`,
      url: SITE_URL,
      name: SITE_NAME,
      description: seo.description,
      publisher: {
        '@id': `${SITE_URL}#organization`
      },
      inLanguage: 'en-IN'
    }
  ];

  if (pathname === '/product') {
    graph.push({
      '@type': 'Product',
      '@id': `${SITE_URL}/product#product`,
      name: PRODUCT.name,
      image: PRODUCT.colors.map((item) => `${SITE_URL}${item.image}`),
      description: PRODUCT.descriptionPoints[0],
      brand: {
        '@type': 'Brand',
        name: 'Divoom'
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'INR',
        price: String(PRODUCT.price),
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/product`,
        itemCondition: 'https://schema.org/NewCondition'
      }
    });
  }

  const payload = {
    '@context': 'https://schema.org',
    '@graph': graph
  };

  let script = document.head.querySelector('#rj-structured-data');
  if (!script) {
    script = document.createElement('script');
    script.setAttribute('id', 'rj-structured-data');
    script.setAttribute('type', 'application/ld+json');
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(payload);
}

export default function RouteExperienceManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  useEffect(() => {
    const seo = SEO_BY_PATH[pathname] || SEO_BY_PATH['/'];
    const canonicalUrl = `${SITE_URL}${pathname}`;

    document.title = seo.title;
    document.documentElement.setAttribute('lang', 'en');

    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'keywords', DEFAULT_KEYWORDS);
    upsertMeta('name', 'robots', seo.robots);
    upsertMeta('name', 'author', 'Rao Jatin Shop');
    upsertMeta('name', 'theme-color', '#101016');
    upsertMeta('name', 'application-name', SITE_NAME);
    upsertMeta('name', 'apple-mobile-web-app-title', SITE_NAME);

    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:type', seo.type || 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:image', DEFAULT_IMAGE);
    upsertMeta('property', 'og:image:secure_url', DEFAULT_IMAGE);
    upsertMeta('property', 'og:image:alt', `${SITE_NAME} logo`);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    upsertMeta('name', 'twitter:image', DEFAULT_IMAGE);
    upsertMeta('name', 'twitter:site', '@raojatin.in');
    upsertMeta('name', 'twitter:creator', '@raojatin.in');

    upsertCanonical(canonicalUrl);
    upsertStructuredData(pathname, seo);
  }, [pathname]);

  return null;
}
